import { AfterViewInit, Component, Input, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { IonModal, ModalController } from "@ionic/angular";
import { LoveItAlertService } from "../../services/love-it-alert.service";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { FairAppIdRequest } from "../../../api/models/fair-app-id-request";
import { KalkulationVorlage } from "../../../api/models/kalkulation-vorlage";
import { OdataGutachtenPosition } from "../../../api/models/odata-gutachten-position";
import { GutachtenService } from "../../../api/services/gutachten.service";
import { getLowerCaseInputValue, getStringFilterConditionForDatatable } from "../../global-functions/global-functions";
import { LoveItToastService } from "../../services/love-it-toast.service";

@Component({
  selector: "app-kalkulation-vorlagen-list",
  templateUrl: "./kalkulation-vorlagen-list.component.html",
  styleUrls: ["./kalkulation-vorlagen-list.component.scss"],
  standalone: false,
})
export class KalkulationVorlagenListComponent implements OnInit, AfterViewInit {
  isSaving: boolean = false;
  isVorlageSaving: boolean = false;
  vorlageSubmitted: boolean = false;
  loadingData: boolean = false;
  loadedData: KalkulationVorlage[] = [];
  @Input()
  currentPositionen: OdataGutachtenPosition[] = [];

  @ViewChild(MatSort) sort: MatSort;
  displayedColumns: string[] = ["bezeichnung", "positionen", "actions"];
  dataSourceMatTable: MatTableDataSource<any>;
  filterFormGroupForDatatable: FormGroup;

  @ViewChild("vorlageErstellenModal") vorlageErstellenModal: IonModal;
  vorlageErstellenBezeichnungFormControl: FormControl;

  @ViewChild("vorlageBearbeitenModal") vorlageBearbeitenModal: IonModal;
  vorlageBearbeitenModalBezeichnungFormControl: FormControl;
  currentlyEditedVorlage: KalkulationVorlage;

  constructor(
    private modalController: ModalController,
    private oidcSecurityService: OidcSecurityService,
    private gutachtenService: GutachtenService,
    private loveItAlertService: LoveItAlertService,
    private loveItToastService: LoveItToastService,
    private fb: FormBuilder,
  ) {
    this.dataSourceMatTable = new MatTableDataSource([]);
  }

  ngOnInit() {
    this.vorlageErstellenBezeichnungFormControl = this.fb.control("", Validators.required);
    this.updateList();

    this.filterFormGroupForDatatable = this.fb.group({
      bezeichnung: [""],
    });

    this.filterFormGroupForDatatable.valueChanges.subscribe(() => {
      this.dataSourceMatTable.data = this.getFilteredDataForDatatable(this.loadedData);
    });
  }

  ngAfterViewInit() {
    this.dataSourceMatTable.sort = this.sort;
  }

  getFilteredDataForDatatable(vorlage: KalkulationVorlage[]) {
    const bezeichnung = getLowerCaseInputValue(this.filterFormGroupForDatatable.get("bezeichnung").value);

    return vorlage.filter((vorlage) => getStringFilterConditionForDatatable(vorlage.bezeichnung, bezeichnung));
  }

  updateList() {
    this.loadingData = true;
    this.dataSourceMatTable.data = [];
    this.gutachtenService
      .gutachtenKalkulationVorlageListCreate({
        token: this.oidcSecurityService.getToken(),
      })
      .subscribe({
        next: (response: KalkulationVorlage[]) => {
          this.loadedData = response.map((entry) => entry);
          this.dataSourceMatTable.data = this.getFilteredDataForDatatable(this.loadedData);
          this.loadingData = false;
        },
        error: (err) => {
          this.loadingData = false;
        },
      });
  }

  removeSavedVorlage(entry: KalkulationVorlage) {
    this.isSaving = true;
    this.gutachtenService
      .gutachtenKalkulationVorlageDeleteCreate({
        token: this.oidcSecurityService.getToken(),
        id: entry.id,
      } as FairAppIdRequest)
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.loadedData = this.loadedData.filter((loadedVorlage) => loadedVorlage.id != entry.id);
          this.dataSourceMatTable.data = this.getFilteredDataForDatatable(this.loadedData);
          this.loveItToastService.showToastMessage("Vorlage gelöscht.");
        },
        error: async (error) => {
          this.isSaving = false;
        },
      });
  }

  async deleteVorlageDialogAnzeigen(entry: KalkulationVorlage) {
    const alert = await this.loveItAlertService.create({
      header: "Vorlage löschen?",
      message: "Die Vorlage ist nach dem Löschen nicht mehr verfügbar.",
      buttons: [
        {
          cssClass: "color-primary",
          text: "Abbrechen",
          role: "cancel",
        },
        {
          cssClass: "color-danger",
          text: "Löschen",
          handler: () => {
            this.removeSavedVorlage(entry);
          },
        },
      ],
    });

    await alert.present();
  }

  cancelButton() {
    this.modalController.dismiss(null, "cancel");
  }

  onDatatableActivate(event: any, vorlage: KalkulationVorlage) {
    if (
      event.target.id === "add-button" ||
      event.target.id === "edit-button" ||
      event.target.id === "delete-button" ||
      this.isSaving
    ) {
      return;
    }
    this.openVorlageBearbeitenModal(vorlage);
  }

  addVorlageToKalkulation(vorlage: KalkulationVorlage) {
    this.modalController.dismiss(vorlage, "confirm");
  }

  openVorlageBearbeitenModal(vorlage: KalkulationVorlage) {
    this.currentlyEditedVorlage = vorlage;
    this.vorlageBearbeitenModalBezeichnungFormControl = new FormControl(
      this.currentlyEditedVorlage?.bezeichnung,
      Validators.required,
    );
    this.vorlageBearbeitenModal.present();
  }

  cancelButtonVorlageBearbeitenModal() {
    this.vorlageBearbeitenModal.dismiss(null, "cancel");
  }

  saveButtonVorlageBearbeitenModal() {
    this.vorlageSubmitted = true;
    if (this.vorlageBearbeitenModalBezeichnungFormControl?.value === "") {
      return;
    }
    this.gutachtenService
      .gutachtenKalkulationVorlageUpdateCreate({
        ...this.currentlyEditedVorlage,
        bezeichnung: this.vorlageBearbeitenModalBezeichnungFormControl?.value,
        token: this.oidcSecurityService.getToken(),
      })
      .subscribe({
        next: (response) => {
          this.vorlageBearbeitenModal.dismiss(null, "confirm");
          this.currentlyEditedVorlage = response;
          this.loadedData.find((entry) => entry.id === response.id).bezeichnung = response?.bezeichnung;
          this.dataSourceMatTable.data = this.getFilteredDataForDatatable(this.loadedData);
          this.isVorlageSaving = false;
        },
        error: (error) => {
          this.isVorlageSaving = false;
        },
      });
  }

  openVorlageErstellenModal() {
    this.vorlageErstellenModal.present();
  }

  cancelButtonVorlageErstellenModal() {
    this.vorlageErstellenModal.dismiss(null, "cancel");
  }

  vorlageErstellen() {
    this.vorlageSubmitted = true;
    if (this.vorlageErstellenBezeichnungFormControl?.value === "") {
      return;
    }
    this.isVorlageSaving = true;
    this.gutachtenService
      .gutachtenKalkulationVorlageCreateCreate({
        kalkulation_positionen: this.currentPositionen,
        bezeichnung: this.vorlageErstellenBezeichnungFormControl?.value,
        token: this.oidcSecurityService.getToken(),
      })
      .subscribe({
        next: (response) => {
          this.loadedData.push(response);
          this.dataSourceMatTable.data = this.getFilteredDataForDatatable(this.loadedData);
          this.vorlageErstellenModal.dismiss(null, "cancel");
          this.vorlageErstellenBezeichnungFormControl?.setValue("");
          this.isVorlageSaving = false;
        },
        error: (error) => {
          this.isVorlageSaving = false;
        },
      });
  }
}
